import { ReactNode } from 'react';

// Structured formula list for column-header tooltips. Each formula line renders
// `label = expression` in tabular-aligned columns; the expression is monospace
// so superscripts and × · ^ symbols line up cleanly. A short description sits
// above the formula block, and an optional `notes` ReactNode sits below for
// per-product caveats or definitions.
export interface FormulaLine {
  label: string;
  expression: ReactNode;
}

export interface HeaderTooltipProps {
  description: ReactNode;
  formulas?: FormulaLine[];
  notes?: ReactNode;
}

export function HeaderTooltip({ description, formulas, notes }: HeaderTooltipProps) {
  return (
    <div className="max-w-sm text-xs leading-snug">
      <div className="text-gray-200">{description}</div>
      {formulas && formulas.length > 0 && (
        <div className="mt-2 space-y-1">
          {formulas.map((f, i) => (
            <div key={i} className="grid grid-cols-[auto_1fr] gap-x-2">
              <span className="font-semibold text-gray-100">{f.label}</span>
              <span className="font-mono text-[11px] tabular-nums text-gray-100">
                = {f.expression}
              </span>
            </div>
          ))}
        </div>
      )}
      {notes && <div className="mt-2 text-[10px] text-gray-300">{notes}</div>}
    </div>
  );
}

// Helper for `(1 + r)^n` style growth factors. Renders the exponent as a real
// `<sup>` so the formula reads cleanly. Pass `simple` for linear riders.
export function GrowthFactor({
  rate,
  exponent,
  simple,
}: {
  rate: string;
  exponent: ReactNode;
  simple?: boolean;
}) {
  if (simple) {
    return (
      <>
        (1 + {rate} × {exponent})
      </>
    );
  }
  return (
    <>
      (1 + {rate})<sup>{exponent}</sup>
    </>
  );
}
